// hardcoded-ui-text: allow — the 'Unknown error' fallbacks here are matched against
// ('Failed to fetch', 'Network request failed') to classify the failure, not rendered.
// SecureAdminPortal.tsx, the only consumer, reads isEnrolled and checkMFAStatus only.
import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface MFAState {
  isEnrolled: boolean;
  isVerified: boolean;
  enrollmentData: { id: string; [key: string]: unknown } | null;
  loading: boolean;
  error: string | null;
  isReachable: boolean;
  hasUnverifiedFactor: boolean;
  existingFactorId: string | null;
}

export function useMFA() {
  const [state, setState] = useState<MFAState>({
    isEnrolled: false,
    isVerified: false,
    enrollmentData: null,
    loading: false,
    error: null,
    isReachable: true,
    hasUnverifiedFactor: false,
    existingFactorId: null,
  });

  const checkingRef = useRef(false);

  const checkMFAStatus = useCallback(async () => {
    // Prevent duplicate calls
    if (checkingRef.current) {
      return { enrolled: state.isEnrolled, verified: state.isVerified, reachable: state.isReachable };
    }
    
    checkingRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Check if user has MFA enrolled
      const { data: factors, error } = await supabase.auth.mfa.listFactors();
      
      if (error) {
        // Check if it's a network/connection error
        const isConnectionError = error.message?.includes('Failed to fetch') || 
                                  error.message?.includes('Network request failed') ||
                                  error.message?.includes('fetch');
        
        setState(prev => ({ 
          ...prev, 
          error: error.message, 
          loading: false,
          isReachable: !isConnectionError
        }));
        return { enrolled: false, verified: false, reachable: !isConnectionError };
      }

      // auth-js listFactors() surfaces ONLY verified factors in factors.totp; an
      // unverified factor appears ONLY in factors.all (GoTrueClient._listFactors:
      // data.all gets every factor, data.totp only status==='verified'). Reading
      // factors.totp here made a half-finished enrollment invisible (enrolled=false,
      // hasUnverified=false) → the cleanup path never fired → the enroll/cleanup loop.
      const totpFactors = (factors?.all ?? []).filter(f => f.factor_type === 'totp');
      const verifiedFactor = totpFactors.find(f => f.status === 'verified');
      const enrolled = totpFactors.length > 0;
      const verified = !!verifiedFactor;
      const hasUnverified = totpFactors.some(f => f.status !== 'verified');
      // Prefer a verified factor's id (login/challenge path); else the unverified one.
      const existingFactorId = (verifiedFactor ?? totpFactors[0])?.id || null;

      setState(prev => ({
        ...prev,
        isEnrolled: enrolled,
        isVerified: verified,
        hasUnverifiedFactor: hasUnverified,
        existingFactorId,
        loading: false,
        isReachable: true,
      }));

      return { enrolled, verified, hasUnverified, reachable: true };
    } catch (error) {
      // Check if it's a network/connection error
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isConnectionError = errorMessage.includes('Failed to fetch') || 
                                errorMessage.includes('Network request failed') ||
                                errorMessage.includes('fetch');
      
      setState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        loading: false,
        isReachable: !isConnectionError
      }));
      return { enrolled: false, verified: false, reachable: !isConnectionError };
    } finally {
      checkingRef.current = false;
    }
  }, [state.isEnrolled, state.isVerified, state.isReachable]);

  const cleanupUnverifiedFactors = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
      // If we can't list factors we can't know what to clean — surface the error
      // instead of the old silent success (which returned true and looped).
      if (listError) {
        setState(prev => ({ ...prev, error: listError.message, loading: false }));
        return false;
      }
      // Unverified factors live ONLY in factors.all (never factors.totp), so the old
      // factors.totp filter was ALWAYS empty → a no-op that "succeeded" while the stale
      // factor persisted. This is the permanent-lockout case: a challenged unverified
      // factor (one wrong code, then abandoned) is never swept by the server, so the
      // client cleanup is the only recovery.
      const unverifiedFactors = (factors?.all ?? []).filter(
        factor => factor.factor_type === 'totp' && factor.status !== 'verified'
      );

      for (const factor of unverifiedFactors) {
        const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
        // A failed unenroll must NOT report success — otherwise the caller re-enrolls
        // and hits the same duplicate-factor 422, re-entering the loop.
        if (unenrollError) {
          setState(prev => ({ ...prev, error: unenrollError.message, loading: false }));
          return false;
        }
      }

      setState(prev => ({
        ...prev,
        loading: false,
        hasUnverifiedFactor: false,
        existingFactorId: null
      }));

      return true;
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Failed to cleanup factors', 
        loading: false 
      }));
      return false;
    }
  }, []);

  const enrollMFA = useCallback(async (forceCleanup = false) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // If there's an unverified factor and we're not forcing cleanup, show error
      if (state.hasUnverifiedFactor && !forceCleanup) {
        setState(prev => ({ 
          ...prev, 
          error: 'An unverified MFA factor already exists. Please clean it up first.', 
          loading: false 
        }));
        return null;
      }
      
      // Clean up any existing unverified factors first
      if (forceCleanup || state.hasUnverifiedFactor) {
        await cleanupUnverifiedFactors();
      }
      
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      });

      if (error) {
        // If we get the "already exists" error, try cleaning up and retrying once
        if (error.message.includes('already exists') && !forceCleanup) {
          console.log('Factor already exists, cleaning up and retrying...');
          const cleaned = await cleanupUnverifiedFactors();
          if (cleaned) {
            return await enrollMFA(true);
          }
        }
        setState(prev => ({ ...prev, error: error.message, loading: false }));
        return null;
      }

      setState(prev => ({
        ...prev,
        enrollmentData: data,
        loading: false,
      }));

      return data;
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Unknown error', 
        loading: false 
      }));
      return null;
    }
  }, [state.hasUnverifiedFactor, cleanupUnverifiedFactors]);

  const verifyMFA = useCallback(async (code: string, challengeId?: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // For enrollment verification, we need to create a challenge first
      if (state.enrollmentData) {
        console.log('Enrollment verification - creating challenge for factor:', state.enrollmentData.id);
        
        // Step 1: Create a challenge for the enrolled factor
        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
          factorId: state.enrollmentData.id,
        });
        
        if (challengeError) {
          console.error('Challenge creation failed:', challengeError);
          setState(prev => ({ ...prev, error: challengeError.message, loading: false }));
          return false;
        }
        
        if (!challengeData?.id) {
          setState(prev => ({ ...prev, error: 'Failed to create challenge', loading: false }));
          return false;
        }
        
        console.log('Challenge created successfully:', challengeData.id);
        
        // Step 2: Verify with the challenge ID and code
        const { error: verifyError } = await supabase.auth.mfa.verify({
          factorId: state.enrollmentData.id,
          challengeId: challengeData.id,
          code,
        });
        
        if (verifyError) {
          console.error('Verification failed:', verifyError);
          setState(prev => ({ ...prev, error: verifyError.message, loading: false }));
          return false;
        }
        
        console.log('Enrollment verification successful');
      } else {
        // For login verification, challengeId should be provided
        if (!challengeId) {
          setState(prev => ({ ...prev, error: 'Challenge ID is required for verification', loading: false }));
          return false;
        }
        
        // Get the factor ID from the first TOTP factor
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totpFactor = factors?.totp?.[0];
        
        if (!totpFactor) {
          setState(prev => ({ ...prev, error: 'No MFA factor found', loading: false }));
          return false;
        }
        
        const { data, error } = await supabase.auth.mfa.verify({
          factorId: totpFactor.id,
          challengeId,
          code,
        });
        
        if (error) {
          setState(prev => ({ ...prev, error: error.message, loading: false }));
          return false;
        }
      }

      setState(prev => ({
        ...prev,
        isVerified: true,
        isEnrolled: true,
        loading: false,
      }));

      return true;
    } catch (error) {
      console.error('MFA verification error:', error);
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Unknown error', 
        loading: false 
      }));
      return false;
    }
  }, [state.enrollmentData]);

  const challengeMFA = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totpFactor = factors?.totp?.[0];
      
      if (!totpFactor) {
        setState(prev => ({ ...prev, error: 'No MFA factor found', loading: false }));
        return null;
      }

      const { data, error } = await supabase.auth.mfa.challenge({
        factorId: totpFactor.id,
      });

      if (error) {
        setState(prev => ({ ...prev, error: error.message, loading: false }));
        return null;
      }

      setState(prev => ({ ...prev, loading: false }));
      return data;
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Unknown error', 
        loading: false 
      }));
      return null;
    }
  }, []);

  const unenrollMFA = useCallback(async (factorId: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { error } = await supabase.auth.mfa.unenroll({
        factorId,
      });

      if (error) {
        setState(prev => ({ ...prev, error: error.message, loading: false }));
        return false;
      }

      setState(prev => ({
        ...prev,
        isEnrolled: false,
        isVerified: false,
        enrollmentData: null,
        hasUnverifiedFactor: false,
        existingFactorId: null,
        loading: false,
      }));

      return true;
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Unknown error', 
        loading: false 
      }));
      return false;
    }
  }, []);

  return {
    ...state,
    checkMFAStatus,
    enrollMFA,
    verifyMFA,
    challengeMFA,
    unenrollMFA,
    cleanupUnverifiedFactors,
  };
}